<script lang="ts">
	import AppLoader from '$lib/components/appLoader/AppLoader.svelte'
	import Favicon from '$lib/components/favicon/Favicon.svelte'
	import Font from '$lib/components/font/Font.svelte'
	import Gtm from '$lib/components/gtm/Gtm.svelte'
	import Tailwind from '$lib/components/layouts/Tailwind.svelte'
	import ScrollbarWidthSetter from '$lib/components/scrollbarWidthSetter/ScrollbarWidthSetter.svelte'
	import { membership } from '$lib/domain/membership/membership'
	import {
		googleCertifiedDomainBase,
		tenantSettings,
	} from '$lib/domain/tenantSettings/tenantSettings'
	import { initializeTracktorApp } from '$lib/settings'
	import { auth, initializeApp } from '@helpers/settings'
	import { ProgressBar } from '@prgm/sveltekit-progress-bar'
	import { initTelemetry } from '@repo/telemetry'
	import { primaryCurrency, setStorageContext, user } from '@repo/ui'
	import { Toasts } from '@repo/ui'
	import { overlays } from '@store/overlays'
	import { tenantImages } from '@store/tenantImages'
	import { applyThemeToBackground, colorTheme } from '@store/theme'
	import { webName } from '@store/webName'
	import { QueryClient, QueryClientProvider } from '@tanstack/svelte-query'
	import { autoLogin } from '@trackdesk/firebase/autoLogin'
	import { createApp, createStorage } from '@trackdesk/firebase/client.js'
	import { signInWithCustomToken } from 'firebase/auth'
	import { onMount } from 'svelte'
	import { queryParameters, ssp } from 'sveltekit-search-params'

	let { data, children } = $props()

	membership.set({
		status: data.membershipStatus,
		endsAt: data.tenantSettings.membershipEndAt,
		planId: data.tenantSettings.membershipPlanId,
	})

	webName.set(data.tenantSettings.tenantDetail.name)
	tenantImages.set({
		lightModeLogo: data.tenantSettings.lightModeLogoUrl,
		darkModeLogo: data.tenantSettings.darkModeLogoUrl,
		favicon: data.tenantSettings.faviconUrl,
	})
	colorTheme.set(data.tenantSettings.colorTheme?.theme || 'blue')
	applyThemeToBackground.set(data.tenantSettings.colorTheme?.backgroundEnabled || false)

	tenantSettings.set(data.tenantSettings)
	primaryCurrency.set(data.tenantSettings.currencyCode)

	googleCertifiedDomainBase.set(data.googleCertifiedBaseDomain)

	let loading = $state(true)

	const queryClient = new QueryClient()

	const params = queryParameters({
		signInToken: ssp.string(),
	})

	const firebaseApp = createApp(data.firebase.config)
	const storage = createStorage(
		data.firebase.config,
		firebaseApp,
		data.firebase.storageEmulatorHost,
	)

	setStorageContext(storage)

	onMount(async () => {
		autoLogin()

		initializeApp(data.environmentDomain, data.firebase, data.recaptcha)

		await initializeTracktorApp(
			data.tenantSettings,
			data.firebase,
			data.recaptcha,
			data.environmentDomain,
			data.featureIds,
			data.featureLimits,
		)

		if (params.signInToken) {
			try {
				await signInWithCustomToken(auth, params.signInToken)
			} finally {
				params.signInToken = null
			}
		}

		loading = false

		if (!import.meta.env.DEV) {
			initTelemetry()
		}
	})

	$effect(() => {
		document.documentElement.classList.toggle('is-logged', Boolean($user))
		document.documentElement.classList.toggle('is-modal-open', $overlays.size > 0)
	})
</script>

{#if !import.meta.env.VITE_DISABLE_GTM}
	<Gtm />
{/if}

<Favicon />

<Font />

<Tailwind>
	<Toasts />

	<AppLoader featureIds={data.featureIds} featureLimits={data.featureLimits} {loading} />

	<ProgressBar class="text-primary-500" />

	<ScrollbarWidthSetter />

	{#if !loading}
		<QueryClientProvider client={queryClient}>
			{@render children?.()}
		</QueryClientProvider>
	{/if}
</Tailwind>

{#if import.meta.env.DEV}
	<style>
		.firebase-emulator-warning {
			display: none;
		}
	</style>
{/if}
